import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw, Calendar, CheckCircle, Clock, XCircle, Eye, Check, Building2, FileText, Edit3, X, User, Mail, Phone, Send } from 'lucide-react';
import { bookingService, apartmentService } from '../../services/api';
import toast from 'react-hot-toast';

interface Booking {
  id: number;
  code_reservation: string;
  apartment: {
    id: number;
    titre: string;
    ville: string;
  };
  locataire?: {
    id: number;
    nom: string;
    prenom: string;
    email: string;
  };
  guest?: {
    name: string;
    email: string;
    phone: string;
  };
  date_debut: string;
  date_fin: string;
  nb_nuits: number;
  nb_personnes: number;
  montant_total: number;
  status: string;
  payment_status: string;
  mode_paiement?: string;
  notes?: string;
  is_deposit: boolean;
  deposit_paid: boolean;
  created_at: string;
}

interface Apartment {
  id: number;
  titre: string;
  ville: string;
}

const AdminBookingsPage: React.FC = () => {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [apartments, setApartments] = useState<Apartment[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal de modification
  const [showModal, setShowModal] = useState(false);
  const [editingBooking, setEditingBooking] = useState<Booking | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [editForm, setEditForm] = useState({
    apartment_id: 0,
    date_debut: '',
    date_fin: '',
    guest_name: '',
    guest_email: '',
    guest_phone: '',
    nb_personnes: 1,
    montant_total: 0,
    notes: '',
    status: 'pending'
  });

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (statusFilter) params.status = statusFilter;
      
      const response = await bookingService.getGerantBookings(params);
      if (response.data?.bookings) {
        setBookings(response.data.bookings);
      }
    } catch (error: any) {
      console.error('Erreur chargement réservations:', error.response?.data || error);
    } finally {
      setLoading(false);
    }
  };

  const fetchApartments = async () => {
    try {
      const response = await apartmentService.getAll({ per_page: 100 });
      if (response.data?.apartments) {
        setApartments(response.data.apartments);
      }
    } catch (error) {
      console.error('Erreur chargement appartements:', error);
    }
  };

  useEffect(() => {
    fetchBookings();
    fetchApartments();
  }, [statusFilter]);

  // Ouvrir le modal de modification
  const openEditModal = (booking: Booking) => {
    setEditingBooking(booking);
    
    let guestName = '';
    let guestEmail = '';
    let guestPhone = '';
    
    if (booking.locataire) {
      guestName = `${booking.locataire.prenom} ${booking.locataire.nom}`;
      guestEmail = booking.locataire.email || '';
    } else if (booking.guest) {
      guestName = booking.guest.name || '';
      guestEmail = booking.guest.email || '';
      guestPhone = booking.guest.phone || '';
    }
    
    setEditForm({
      apartment_id: booking.apartment?.id || 0,
      date_debut: booking.date_debut.split('T')[0],
      date_fin: booking.date_fin.split('T')[0],
      guest_name: guestName,
      guest_email: guestEmail,
      guest_phone: guestPhone,
      nb_personnes: booking.nb_personnes,
      montant_total: booking.montant_total,
      notes: booking.notes || '',
      status: booking.status
    });
    setShowModal(true);
  };

  // Soumettre la modification
  const handleSubmitEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingBooking) return;
    
    if (!editForm.guest_name || !editForm.guest_phone) {
      toast.error('Veuillez remplir le nom et téléphone du client');
      return;
    }
    
    if (new Date(editForm.date_fin) <= new Date(editForm.date_debut)) {
      toast.error('La date de fin doit être après la date de début');
      return;
    }
    
    setSubmitting(true);
    try {
      await bookingService.updateGerant(editingBooking.id, {
        apartment_id: editForm.apartment_id,
        date_debut: editForm.date_debut,
        date_fin: editForm.date_fin,
        guest_name: editForm.guest_name,
        guest_email: editForm.guest_email || undefined,
        guest_phone: editForm.guest_phone,
        nb_personnes: editForm.nb_personnes,
        montant_total: editForm.montant_total,
        notes: editForm.notes || undefined,
        status: editForm.status
      });
      
      toast.success('Réservation modifiée avec succès !');
      setShowModal(false);
      setEditingBooking(null);
      fetchBookings();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erreur lors de la modification');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmCash = async (bookingId: number) => {
    setActionLoading(bookingId);
    try {
      await bookingService.confirmCashPayment(bookingId);
      await fetchBookings();
    } catch (error: any) {
      console.error('Erreur confirmation:', error.response?.data || error);
      alert(error.response?.data?.error || 'Erreur lors de la confirmation');
    } finally {
      setActionLoading(null);
    }
  };

  const handleConfirmDeposit = async (bookingId: number) => {
    setActionLoading(bookingId);
    try {
      await bookingService.confirmDepositPayment(bookingId);
      toast.success('Acompte validé ! La facture est disponible.');
      await fetchBookings();
    } catch (error: any) {
      console.error('Erreur confirmation acompte:', error.response?.data || error);
      toast.error(error.response?.data?.error || 'Erreur lors de la confirmation');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancelBooking = async (bookingId: number, code: string) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir annuler la réservation #${code} ?`)) {
      return;
    }
    
    setActionLoading(bookingId);
    try {
      await bookingService.cancel(bookingId);
      toast.success('Réservation annulée avec succès');
      await fetchBookings();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erreur lors de l\'annulation');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDownloadInvoice = async (bookingId: number, code: string) => {
    try {
      const response = await bookingService.downloadInvoice(bookingId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `facture_${code}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erreur téléchargement facture');
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'confirmed':
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"><CheckCircle className="w-3 h-3" /> Confirmée</span>;
      case 'pending':
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800"><Clock className="w-3 h-3" /> En attente</span>;
      case 'cancelled':
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800"><XCircle className="w-3 h-3" /> Annulée</span>;
      case 'completed':
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"><CheckCircle className="w-3 h-3" /> Terminée</span>;
      default:
        return <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">{status}</span>;
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('fr-FR').format(price) + ' FCFA';
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getClientName = (booking: Booking) => {
    if (booking.locataire) return `${booking.locataire.prenom} ${booking.locataire.nom}`;
    if (booking.guest) return `${booking.guest.name} (Invité)`;
    return 'Client inconnu';
  };

  const getClientEmail = (booking: Booking) => {
    if (booking.locataire) return booking.locataire.email;
    if (booking.guest) return booking.guest.email;
    return '';
  };

  const filteredBookings = bookings.filter(booking => {
    const search = searchTerm.toLowerCase();
    return (
      booking.code_reservation.toLowerCase().includes(search) ||
      booking.apartment?.titre.toLowerCase().includes(search) ||
      getClientName(booking).toLowerCase().includes(search) ||
      getClientEmail(booking).toLowerCase().includes(search)
    );
  });

  const pendingCount = bookings.filter(b => b.status === 'pending').length;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-heading font-bold text-neutral-900">Toutes les Réservations</h1>
          <p className="text-neutral-600">{bookings.length} réservation{bookings.length > 1 ? 's' : ''} au total</p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/admin/bookings/invitation/new"
            className="btn btn-primary flex items-center gap-2"
          >
            <Send className="w-4 h-4" />
            Créer une invitation
          </Link>
          <button onClick={fetchBookings} className="btn btn-outline flex items-center gap-2">
            <RefreshCw className="w-4 h-4" />
            Actualiser
          </button>
        </div>
      </div>

      {/* Alerte réservations en attente */}
      {pendingCount > 0 && (
        <div className="bg-orange-50 border-2 border-orange-300 rounded-xl p-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center">
              <Clock className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="font-bold text-orange-800 text-lg">
                {pendingCount} réservation{pendingCount > 1 ? 's' : ''} en attente de validation
              </p>
              <p className="text-orange-700 text-sm">Validez les paiements pour confirmer ces réservations</p>
            </div>
          </div>
        </div>
      )}

      {/* Filtres */}
      <div className="card p-4 flex flex-col md:flex-row gap-4">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Rechercher par code, appartement ou client..."
          className="flex-1 border border-gray-300 rounded-lg p-3"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="border border-gray-300 rounded-lg p-3"
        >
          <option value="">Tous les statuts</option>
          <option value="pending">En attente</option>
          <option value="confirmed">Confirmées</option>
          <option value="completed">Terminées</option>
          <option value="cancelled">Annulées</option>
        </select>
      </div>

      {/* Liste des réservations */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
        </div>
      ) : filteredBookings.length === 0 ? (
        <div className="card p-12 text-center">
          <Calendar className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
          <p className="text-neutral-500">
            {searchTerm || statusFilter ? 'Aucune réservation trouvée pour ces critères' : 'Aucune réservation sur la plateforme'}
          </p>
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredBookings.map((booking) => (
            <div 
              key={booking.id} 
              className={`card p-4 ${booking.status === 'pending' ? 'border-2 border-orange-300 bg-orange-50' : ''}`}
            >
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="font-mono text-sm bg-neutral-100 px-2 py-1 rounded">
                      #{booking.code_reservation}
                    </span>
                    {getStatusBadge(booking.status)}
                    {booking.is_deposit && (
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        booking.deposit_paid ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {booking.deposit_paid ? 'Acompte payé' : 'Acompte'}
                      </span>
                    )}
                  </div>
                  
                  <div className="space-y-1 text-sm">
                    <p className="flex items-center gap-2 font-medium">
                      <Building2 className="w-4 h-4 text-primary-500" />
                      {booking.apartment?.titre} - {booking.apartment?.ville}
                    </p>
                    <p className="text-neutral-600">
                      <strong>Client:</strong> {getClientName(booking)}
                      {getClientEmail(booking) && <span className="text-neutral-400"> ({getClientEmail(booking)})</span>}
                    </p>
                    <p className="flex items-center gap-2 text-neutral-600">
                      <Calendar className="w-4 h-4" />
                      {formatDate(booking.date_debut)} → {formatDate(booking.date_fin)}
                      <span className="text-neutral-400">({booking.nb_nuits} nuit{booking.nb_nuits > 1 ? 's' : ''})</span>
                    </p>
                    <p className="font-semibold text-primary-600">
                      {formatPrice(booking.montant_total)}
                    </p>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-2">
                  {booking.status === 'pending' && (
                    <>
                      {booking.is_deposit && !booking.deposit_paid ? (
                        <button
                          onClick={() => handleConfirmDeposit(booking.id)}
                          disabled={actionLoading === booking.id}
                          className="btn bg-blue-500 text-white hover:bg-blue-600 flex items-center gap-2"
                        >
                          {actionLoading === booking.id ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <Check className="w-4 h-4" />
                          )}
                          Valider acompte
                        </button>
                      ) : (
                        <button
                          onClick={() => handleConfirmCash(booking.id)}
                          disabled={actionLoading === booking.id}
                          className="btn bg-green-500 text-white hover:bg-green-600 flex items-center gap-2"
                        >
                          {actionLoading === booking.id ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <Check className="w-4 h-4" />
                          )}
                          Valider paiement
                        </button>
                      )}
                    </>
                  )}
                  {/* Bouton Modifier */}
                  {booking.status !== 'cancelled' && (
                    <button
                      onClick={() => openEditModal(booking)}
                      className="btn btn-outline btn-sm flex items-center gap-1"
                    >
                      <Edit3 className="w-4 h-4" />
                      Modifier
                    </button>
                  )}
                  
                  {/* Bouton télécharger facture (si paiement validé - acompte ou complet) */}
                  {((booking.is_deposit && booking.deposit_paid) || 
                    (booking.status === 'confirmed' && booking.payment_status === 'completed')) && (
                    <button
                      onClick={() => handleDownloadInvoice(booking.id, booking.code_reservation)}
                      className="btn btn-outline btn-sm flex items-center gap-1"
                      title="Télécharger la facture"
                    >
                      <FileText className="w-4 h-4" />
                      Facture
                    </button>
                  )}
                  
                  {/* Bouton Annuler la réservation */}
                  {booking.status !== 'cancelled' && booking.status !== 'completed' && (
                    <button
                      onClick={() => handleCancelBooking(booking.id, booking.code_reservation)}
                      disabled={actionLoading === booking.id}
                      className="btn bg-red-500 text-white hover:bg-red-600 btn-sm flex items-center gap-1"
                      title="Annuler la réservation"
                    >
                      {actionLoading === booking.id ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <XCircle className="w-4 h-4" />
                      )}
                      Annuler
                    </button>
                  )}
                  
                  <button
                    onClick={() => window.open(`/apartments/${booking.apartment?.id}`, '_blank')}
                    className="btn btn-outline btn-sm flex items-center gap-1"
                  >
                    <Eye className="w-4 h-4" />
                    Voir appart.
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal de modification */}
      {showModal && editingBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-xl font-semibold">Modifier la réservation</h2>
              <button
                onClick={() => { setShowModal(false); setEditingBooking(null); }}
                className="p-2 hover:bg-neutral-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-4 pt-3 pb-0">
              <span className="text-sm text-neutral-500">Code: </span>
              <span className="font-mono font-medium text-primary-600">{editingBooking.code_reservation}</span>
            </div>
            
            <form onSubmit={handleSubmitEdit} className="p-4 space-y-4">
              {/* Appartement */}
              <div>
                <label className="block text-sm font-medium mb-1">Appartement</label>
                <select
                  value={editForm.apartment_id}
                  onChange={(e) => setEditForm({...editForm, apartment_id: Number(e.target.value)})}
                  className="input w-full"
                  required
                >
                  <option value="">Sélectionner un appartement</option>
                  {apartments.map(apt => (
                    <option key={apt.id} value={apt.id}>{apt.titre} - {apt.ville}</option>
                  ))}
                </select>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Date d'arrivée</label>
                  <input
                    type="date"
                    value={editForm.date_debut}
                    onChange={(e) => setEditForm({...editForm, date_debut: e.target.value})}
                    className="input w-full"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Date de départ</label>
                  <input
                    type="date"
                    value={editForm.date_fin}
                    onChange={(e) => setEditForm({...editForm, date_fin: e.target.value})}
                    className="input w-full"
                    required
                  />
                </div>
              </div>

              {/* Infos client */}
              <div className="space-y-3">
                <label className="block text-sm font-medium">Informations client</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                  <input
                    type="text"
                    value={editForm.guest_name}
                    onChange={(e) => setEditForm({...editForm, guest_name: e.target.value})}
                    className="input w-full pl-10"
                    placeholder="Nom du client"
                    required
                  />
                </div>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                  <input
                    type="tel"
                    value={editForm.guest_phone}
                    onChange={(e) => setEditForm({...editForm, guest_phone: e.target.value})}
                    className="input w-full pl-10"
                    placeholder="+221 77 123 45 67"
                    required
                  />
                </div>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                  <input
                    type="email"
                    value={editForm.guest_email}
                    onChange={(e) => setEditForm({...editForm, guest_email: e.target.value})}
                    className="input w-full pl-10"
                    placeholder="email@exemple.com"
                  />
                </div>
              </div>

              {/* Personnes et montant */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Personnes</label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={editForm.nb_personnes}
                    onChange={(e) => setEditForm({...editForm, nb_personnes: Number(e.target.value)})}
                    className="input w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Montant (FCFA)</label>
                  <input
                    type="number"
                    min="0"
                    value={editForm.montant_total}
                    onChange={(e) => setEditForm({...editForm, montant_total: Number(e.target.value)})}
                    className="input w-full"
                  />
                </div>
              </div>

              {/* Statut */}
              <div>
                <label className="block text-sm font-medium mb-1">Statut</label>
                <select
                  value={editForm.status}
                  onChange={(e) => setEditForm({...editForm, status: e.target.value})}
                  className="input w-full"
                >
                  <option value="pending">En attente</option>
                  <option value="confirmed">Confirmée</option>
                  <option value="completed">Terminée</option>
                  <option value="cancelled">Annulée</option>
                </select>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium mb-1">Notes</label>
                <textarea
                  value={editForm.notes}
                  onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                  className="input w-full"
                  rows={2}
                  placeholder="Notes internes..."
                />
              </div>

              {/* Boutons */}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); setEditingBooking(null); }}
                  className="btn btn-outline flex-1"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn btn-primary flex-1"
                >
                  {submitting ? 'Enregistrement...' : 'Enregistrer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBookingsPage;
